import { UseMutateFunction, useMutation, useQuery, useQueryClient } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { nonBlockedCreditors, useEfakturaCreditors } from '../../../fetchHooks/useEfaktura';
import type {
    EFakturaCreditor,
    EFakturaGivenPermission,
    EFakturaPermissionId,
    EFakturaReceivedPermission,
} from '../../../generated-types';
import {
    deleteGivenEfakturaPermission,
    deleteReceivedEfakturaPermission,
    getGivenSharedEfakturas,
    getReceivedSharedEfakturas,
} from '../../../services/eFakturaService';
import { ErrorTypes } from '../../../utils/gtm/errorTypes';
import { pushErrorToDataLayer } from '../../../utils/gtm/gtmService';

type PartialQueryResult<T = unknown> = {
    isLoading: boolean;
    error?: RequestExceptionWithResponse | null;
    data: T;
    refetch?: () => void;
    invalidateQuery?: () => Promise<void>;
};

type PartialMutationResult<TResponse = unknown, TPayload = unknown> = {
    isLoading: boolean;
    error?: RequestExceptionWithResponse | null;
    mutate: UseMutateFunction<TResponse, RequestExceptionWithResponse, TPayload>;
    mutateAsync: UseMutateFunction<TResponse, RequestExceptionWithResponse, TPayload>;
    data?: TResponse;
    reset: () => void;
    isSuccess: boolean;
};

export type SharedEfakturaType = {
    isLoading: boolean;
    creditors: PartialQueryResult<EFakturaCreditor[]>;
    givenEfakturas: PartialQueryResult<EFakturaGivenPermission[]>;
    receivedEfakturas: PartialQueryResult<EFakturaReceivedPermission[]>;
    deleteGiven: PartialMutationResult<void, EFakturaPermissionId>;
    deleteReceived: PartialMutationResult<void, EFakturaPermissionId>;
};

const INCOMING_EFAKTURAS_KEY = ['incoming-shared-efaktura'];
const OUTGOING_EFAKTURAS_KEY = ['outgoing-shared-efaktura'];

const useSharedEfaktura = (): SharedEfakturaType => {
    const queryClient = useQueryClient();
    const creditors = useEfakturaCreditors({ select: nonBlockedCreditors });

    const incomingEfakturas = useQuery<EFakturaGivenPermission[]>({
        queryKey: INCOMING_EFAKTURAS_KEY,
        queryFn: getGivenSharedEfakturas,
    });
    const invalidateIncomingEfakturas = () => queryClient.invalidateQueries({ queryKey: INCOMING_EFAKTURAS_KEY });

    const outgoingEfakturas = useQuery<EFakturaReceivedPermission[]>({
        queryKey: OUTGOING_EFAKTURAS_KEY,
        queryFn: getReceivedSharedEfakturas,
    });
    const invalidateOutgoingEfakturas = () => queryClient.invalidateQueries({ queryKey: OUTGOING_EFAKTURAS_KEY });

    const deleteGivenShared = useMutation<void, RequestExceptionWithResponse, EFakturaPermissionId>({
        mutationKey: ['delete-given-shared-efaktura'],
        mutationFn: (id) => deleteGivenEfakturaPermission(id),
        onSuccess: invalidateIncomingEfakturas,
        onError: (error: Error) => pushErrorToDataLayer(error, ErrorTypes.DELETE_GIVEN_EFAKTURA),
    });
    const deleteRecievedShared = useMutation<void, RequestExceptionWithResponse, EFakturaPermissionId>({
        mutationKey: ['delete-received-shared-efaktura'],
        mutationFn: deleteReceivedEfakturaPermission,
        onSuccess: invalidateOutgoingEfakturas,
        onError: (error: Error) => pushErrorToDataLayer(error, ErrorTypes.DELETE_RECEIVED_EFAKTURA),
    });

    return {
        isLoading: creditors.isLoading || incomingEfakturas.isLoading || outgoingEfakturas.isLoading,
        creditors: {
            data: creditors.data ?? [],
            isLoading: creditors.isLoading,
            error: creditors.error,
            refetch: creditors.refetch,
        },
        givenEfakturas: {
            data: incomingEfakturas.data ?? [],
            isLoading: incomingEfakturas.isLoading,
            error: incomingEfakturas.error,
            refetch: incomingEfakturas.refetch,
            invalidateQuery: invalidateIncomingEfakturas,
        },
        receivedEfakturas: {
            data: outgoingEfakturas.data ?? [],
            isLoading: outgoingEfakturas.isLoading,
            error: outgoingEfakturas.error,
            refetch: outgoingEfakturas.refetch,
            invalidateQuery: invalidateOutgoingEfakturas,
        },
        deleteGiven: {
            mutate: deleteGivenShared.mutate,
            mutateAsync: deleteGivenShared.mutateAsync,
            isLoading: deleteGivenShared.isPending,
            error: deleteGivenShared.error,
            isSuccess: deleteGivenShared.isSuccess,
            reset: deleteGivenShared.reset,
        },
        deleteReceived: {
            mutate: deleteRecievedShared.mutate,
            mutateAsync: deleteRecievedShared.mutateAsync,
            isLoading: deleteRecievedShared.isPending,
            error: deleteRecievedShared.error,
            isSuccess: deleteRecievedShared.isSuccess,
            reset: deleteRecievedShared.reset,
        },
    };
};

export default useSharedEfaktura;
